/*
 * Moteur de scoring CYCLIQ PROFILE V2 : portage JavaScript strict du moteur
 * Python (scoring.py, barème 2.0.0).
 *
 * Trois moteurs strictement séparés :
 * - CYCLIQ PROFILE (ce module) : bilan personnel calculé localement.
 * - CYCLIQ SAFETY : safety_flag local, jamais transmis, jamais commercial.
 * - CYCLIQ FIT : qualification commerciale séparée (lib/fitEngine.js),
 *   affichée uniquement après la restitution du bilan.
 *
 * Conservation nulle : ce moteur s'exécute uniquement dans le navigateur.
 * Les réponses et les résultats ne quittent jamais la session.
 *
 * Règles :
 * - Échelles 0 à 10 : valeur x 10. Échelle INVERSÉE Q10 : 100 moins valeur x 10.
 * - Question masquée, non répondue, refusée ou option explicitement exclue
 *   (Q8 je ne sais pas, Q13 je pratique trop peu) : exclue du dénominateur,
 *   jamais une pénalité.
 * - Score global : moyenne pondérée renormalisée des dimensions calculables.
 * - Q1 à Q4 et Q24 ne notent pas le score (contexte, adaptation, prudence).
 * - Arrondi identique à Python (arrondi bancaire au pair le plus proche).
 */

export const BAREME_VERSION = "2.0.0";

export const DEFAULT_SCORING_CONFIG = {
  bareme_version: BAREME_VERSION,
  dimension_weights: {
    sync: 0.25,
    energie: 0.25,
    entrainement: 0.15,
    nutrition: 0.15,
    constance: 0.20,
  },
  profile_ready_threshold: 70,
  inverted_questions: ["q14", "q20", "q21", "q23"],
  inverted_scales: ["q10"],
  restitution_levels: [
    { key: "prioritaire", label: "À renforcer en priorité", min: 0 },
    { key: "construction", label: "En construction", min: 40 },
    { key: "solide", label: "Solide", min: 70 },
  ],
};

const CHOICE_SCORES = {
  q5: { clairement: 100, parfois: 70, rarement: 40, ne_sais_pas: 20 },
  q6: { regulierement: 100, parfois: 70, rarement: 40, jamais: 20 },
  q8: { jamais_presque: 100, parfois: 70, regulierement: 40, souvent: 20, ne_sais_pas: null },
  q9: { precisement: 100, parfois: 70, rarement: 40, pas_du_tout: 20, jamais_pense: 10 },
  q13: { facile_previsible: 100, correcte_variable: 70, plus_difficile: 30, tres_variable: 40, pratique_trop_peu: null },
  q14: { rarement: 100, quelques_fois_mois: 70, plusieurs_fois_semaine: 30, quotidiennement: 5 },
  q15: { aucune: 20, une: 50, deux: 70, trois: 90, quatre_plus: 100 },
  q16: { oui: 100, partiellement: 70, non: 30, pas_de_programme: 20 },
  q17: { oui_indicateurs: 100, parfois: 70, rarement: 40, jamais: 20 },
  q19: { tres_coherente: 100, plutot_coherente: 75, irreguliere: 45, difficile: 20, ne_sais_pas: 40 },
  q20: { rarement: 100, parfois: 70, plusieurs_fois_semaine: 30, quotidiennement: 5 },
  q21: { rarement: 100, parfois: 70, souvent: 30, tres_souvent: 5 },
  q22: { oui: 100, partiellement: 60, non: 20 },
  q23: { rarement: 100, parfois: 70, souvent: 30, presque_toujours: 5 },
};

const SCALE_QUESTIONS = new Set(["q7", "q10", "q11", "q12", "q18"]);
const INVERTED_SCALES = new Set(["q10"]);
const SCALE_BOUNDS = {
  q7: { min: 0, max: 10 },
  q10: { min: 0, max: 10 },
  q11: { min: 0, max: 10 },
  q12: { min: 0, max: 10 },
  q18: { min: 0, max: 10 },
};

const PREFER_NOT_TO_ANSWER = "prefere_ne_pas_repondre";

const DEFAULT_SKIP_RULES = [
  { question: "q17", skip_if: { q16: ["pas_de_programme"] } },
];

const ALL_QUESTION_IDS = [
  "q1", "q2", "q3", "q4", "q5", "q6", "q7", "q8", "q9", "q10", "q11", "q12",
  "q13", "q14", "q15", "q16", "q17", "q18", "q19", "q20", "q21", "q22", "q23", "q24",
];

export const DIMENSIONS = [
  {
    key: "sync",
    label: "Compréhension et synchronisation",
    weight: 0.25,
    questions: ["q5", "q6", "q7", "q8", "q9", "q10"],
    phrase: "ta compréhension de ton fonctionnement cyclique",
    phrase_no_cycle: "ta compréhension de tes variations individuelles",
    force_text: "Tu perçois déjà tes variations et tu commences à t'y adapter. C'est une base solide pour aller plus loin.",
    priority_text: "Mieux identifier tes variations individuelles te permettrait d'ajuster ton activité et ta récupération au bon moment.",
  },
  {
    key: "energie",
    label: "Énergie, sommeil et récupération",
    weight: 0.25,
    questions: ["q11", "q12", "q13", "q14"],
    phrase: "ton énergie et ta récupération",
    phrase_no_cycle: "ton énergie et ta récupération",
    force_text: "Ton énergie et ta récupération constituent un appui fiable pour progresser sans t'épuiser.",
    priority_text: "Restaurer une énergie plus stable et une meilleure récupération semble être un levier prioritaire pour toi.",
  },
  {
    key: "entrainement",
    label: "Cohérence de l'activité physique",
    weight: 0.15,
    questions: ["q15", "q16", "q17", "q18"],
    phrase: "la structure de ton activité physique",
    phrase_no_cycle: "la structure de ton activité physique",
    force_text: "Ta pratique est structurée et régulière : un vrai atout pour construire des résultats durables.",
    priority_text: "Un cadre d'activité plus personnalisé et mesurable t'aiderait à transformer tes efforts en progression visible.",
  },
  {
    key: "nutrition",
    label: "Nutrition et habitudes",
    weight: 0.15,
    questions: ["q19", "q20", "q21"],
    phrase: "la structure de ton alimentation",
    phrase_no_cycle: "la structure de ton alimentation",
    force_text: "Tes habitudes alimentaires soutiennent déjà ton objectif. Tu peux t'appuyer dessus.",
    priority_text: "Structurer progressivement ton alimentation rendrait tes journées plus simples et plus efficaces.",
  },
  {
    key: "constance",
    label: "Charge mentale et constance",
    weight: 0.20,
    questions: ["q22", "q23"],
    phrase: "ta charge mentale et ta constance",
    phrase_no_cycle: "ta charge mentale et ta constance",
    force_text: "Tu sais maintenir le cap même quand la motivation varie. Cette constance est précieuse.",
    priority_text: "Sécuriser ta constance avec une méthode simple t'éviterait de repartir de zéro à chaque période chargée.",
  },
];

const DIMENSION_INDEX = Object.fromEntries(DIMENSIONS.map((d) => [d.key, d]));

const RESTITUTION_DIMENSIONS = ["sync", "energie", "entrainement", "nutrition", "constance"];

// Ordre technique de départage en cas d'égalité parfaite (score puis poids).
const TIE_BREAK_ORDER = ["sync", "energie", "constance", "nutrition", "entrainement"];

export const PROFILES = {
  energie_a_reconstruire: {
    label: "Énergie à reconstruire",
    description: "Ton corps semble réclamer une base d'énergie plus stable avant d'accumuler davantage d'efforts. En réorganisant tes appuis, comme la récupération, le rythme et la structure, ta progression peut devenir beaucoup plus fluide.",
  },
  recuperation_a_soutenir: {
    label: "Récupération à soutenir",
    description: "Tes efforts sont réels, mais ta récupération semble aujourd'hui le maillon qui mérite le plus d'attention. Mieux récupérer, c'est souvent progresser plus sereinement, sans en faire plus.",
  },
  structure_activite_a_consolider: {
    label: "Structure d'activité à consolider",
    description: "Ta volonté est là, mais ton cadre d'activité physique ne semble pas encore aligné avec ton objectif et ton quotidien. Une structure réellement personnalisée et mesurable changerait la donne.",
  },
  habitudes_a_structurer: {
    label: "Habitudes à structurer",
    description: "Ton alimentation semble être le domaine qui mérite le plus d'attention aujourd'hui. Pas de régime rigide : une structure simple, adaptée à tes journées réelles, suffit souvent à tout changer.",
  },
  constance_a_reconstruire: {
    label: "Constance à reconstruire",
    description: "Tu sais faire des efforts. Ce qui te manque, c'est un système qui tient quand la vie s'accélère. Reconstruire ta constance est probablement ton levier de progression le plus puissant.",
  },
  reperes_a_clarifier: {
    label: "Repères corporels à clarifier",
    description: "Tu avances, mais sans carte complète de ton propre fonctionnement. Mieux repérer tes variations te permettrait de placer tes efforts au bon moment et d'avancer avec ton fonctionnement plutôt que contre lui.",
  },
  profil_equilibre: {
    label: "Profil globalement équilibré",
    description: "Tes réponses font apparaître plusieurs bases déjà solides. Ton enjeu ne semble pas être de tout reconstruire, mais plutôt d'identifier les ajustements les plus pertinents pour ton fonctionnement personnel.",
  },
};

export const ACTIONS_LIBRARY = {
  sync: [
    "Note chaque soir pendant 2 semaines ton niveau d'énergie sur 10 : tu verras apparaître tes propres tendances.",
    "Repère les moments du mois où tes journées te semblent plus faciles et note ces moments sans chercher à les expliquer.",
    "Choisis un seul indicateur (énergie, humeur ou sommeil) et prends le temps de l'observer sans jugement pendant 14 jours.",
  ],
  energie: [
    "Fixe une heure de coucher réaliste et tiens cette heure 5 soirs sur 7 pendant 2 semaines.",
    "Ajoute 10 minutes de marche lente après ton repas du midi pour soutenir ton énergie du reste de la journée.",
    "Prévois une vraie pause de 5 minutes sans écran au milieu de ta journée la plus chargée.",
  ],
  entrainement: [
    "Planifie tes séances de la semaine le dimanche soir, avec un plan B de 20 minutes pour les jours saturés.",
    "Choisis un repère de progression simple, par exemple une durée, une distance ou un nombre de répétitions, et note ce repère à chaque séance.",
    "Commence par 2 séances fixes par semaine plutôt que 4 aléatoires : la régularité précède le volume.",
  ],
  nutrition: [
    "Prépare ton repas du matin ou ton repas du midi la veille, 3 jours cette semaine.",
    "Pose une bouteille d'eau visible sur ton bureau et termine cette bouteille avant 16 heures.",
    "Ajoute une source de protéines et une portion de légumes à ton repas principal, sans rien supprimer d'autre.",
  ],
  constance: [
    "Définis ta version minimale d'une séance (10 minutes suffisent) pour les semaines où tout déborde.",
    "Accroche ta pratique à une habitude existante : après le café du matin, avant la douche du soir.",
    "Note chaque petite victoire de la semaine : la constance se construit sur ce qui est fait, pas sur ce qui manque.",
  ],
  prete: [
    "Formalise ton objectif principal en une phrase mesurable : ce que tu veux, pour quand, et comment tu le vérifieras.",
    "Identifie la dimension que tu veux affiner en premier et concentre tes 2 prochaines semaines dessus.",
    "Prépare 3 questions précises à poser lors de ton entretien pour gagner du temps sur ton plan d'action.",
  ],
  prudence: [
    "Prends contact avec le professionnel de santé qui suit ta situation et clarifie avec lui ce qui est possible aujourd'hui pour ton activité physique.",
    "Prépare pour cet échange une liste de tes objectifs et de tes questions : des demandes précises obtiennent des réponses précises.",
    "Note pendant quelques jours tes ressentis, énergie, sommeil et éventuelles gênes, pour donner une vision claire et factuelle de ta situation.",
  ],
};

/* ------------------------------------------------------------------ */
/* CYCLIQ SAFETY : indicateur local, jamais transmis, jamais commercial */
/* ------------------------------------------------------------------ */

const SAFETY_FLAGS = {
  non: "clear",
  oui: "orientation_required",
  ne_sais_pas: "unknown",
  prefere_ne_pas_repondre: "private",
};

export function computeSafetyFlag(answers) {
  return SAFETY_FLAGS[answers?.q24] || "private";
}

export function requiresCaution(safetyFlag) {
  return safetyFlag === "orientation_required" || safetyFlag === "unknown";
}

/* ------------------------------------------------------------------ */
/* Arrondi identique à Python : arrondi bancaire au pair le plus proche */
/* ------------------------------------------------------------------ */

function pyRound(x) {
  const floor = Math.floor(x);
  const diff = x - floor;
  if (diff < 0.5) return floor;
  if (diff > 0.5) return floor + 1;
  return floor % 2 === 0 ? floor : floor + 1;
}

/* ------------------------------------------------------------------ */
/* Fonctions de calcul                                                  */
/* ------------------------------------------------------------------ */

function questionIsSkipped(qid, answers, skipRules) {
  for (const rule of skipRules || DEFAULT_SKIP_RULES) {
    if (rule.question === qid) {
      for (const [triggerQ, triggerValues] of Object.entries(rule.skip_if || {})) {
        if (triggerValues.includes(answers[triggerQ])) return true;
      }
    }
  }
  return false;
}

export function questionScore(qid, answers, skipRules) {
  if (questionIsSkipped(qid, answers, skipRules)) return null;
  const raw = answers[qid];
  if (raw === null || raw === undefined || raw === "" || raw === PREFER_NOT_TO_ANSWER) return null;
  if (SCALE_QUESTIONS.has(qid)) {
    const value = Number(raw);
    if (Number.isNaN(value)) return null;
    const bounds = SCALE_BOUNDS[qid] || { min: 0, max: 10 };
    const clamped = Math.max(bounds.min, Math.min(bounds.max, value));
    if (INVERTED_SCALES.has(qid)) return 100.0 - clamped * 10.0;
    return clamped * 10.0;
  }
  const mapping = CHOICE_SCORES[qid];
  if (!mapping) return null;
  const score = mapping[String(raw)];
  return score === undefined || score === null ? null : score;
}

export function computeDimensionScores(answers, skipRules) {
  const scores = {};
  for (const dim of DIMENSIONS) {
    const values = dim.questions
      .map((q) => questionScore(q, answers, skipRules))
      .filter((v) => v !== null);
    scores[dim.key] = values.length ? pyRound(values.reduce((a, b) => a + b, 0) / values.length) : null;
  }
  return scores;
}

function mergedConfig(scoringConfig) {
  const config = { ...DEFAULT_SCORING_CONFIG, ...(scoringConfig || {}) };
  config.dimension_weights = {
    ...DEFAULT_SCORING_CONFIG.dimension_weights,
    ...((scoringConfig || {}).dimension_weights || {}),
  };
  return config;
}

export function computeGlobalScore(dimensionScores, weights) {
  const w = weights || DEFAULT_SCORING_CONFIG.dimension_weights;
  let totalWeight = 0.0;
  let weightedSum = 0.0;
  for (const dim of DIMENSIONS) {
    const score = dimensionScores[dim.key];
    if (score !== null && score !== undefined) {
      const weight = w[dim.key] !== undefined ? w[dim.key] : dim.weight;
      totalWeight += weight;
      weightedSum += score * weight;
    }
  }
  if (totalWeight === 0) return null;
  return pyRound(weightedSum / totalWeight);
}

export function computeEnergySubScores(answers, skipRules) {
  const avg = (qids) => {
    const values = qids.map((q) => questionScore(q, answers, skipRules)).filter((v) => v !== null);
    return values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
  };
  return { energie_sub: avg(["q11", "q14"]), recuperation_sub: avg(["q12", "q13"]) };
}

// Table de correspondance dimension la plus faible -> profil (hors cas energie).
const PROFILE_BY_WEAKEST_DIMENSION = {
  sync: "reperes_a_clarifier",
  entrainement: "structure_activite_a_consolider",
  nutrition: "habitudes_a_structurer",
  constance: "constance_a_reconstruire",
};

function weakestRestitutionKey(restitution, weights) {
  // Dimension la plus faible ; egalite -> poids le plus eleve, puis ordre technique.
  const sorted = [...restitution].sort((a, b) => {
    if (a[1] !== b[1]) return a[1] - b[1];
    const wa = weights[a[0]] !== undefined ? weights[a[0]] : DIMENSION_INDEX[a[0]].weight;
    const wb = weights[b[0]] !== undefined ? weights[b[0]] : DIMENSION_INDEX[b[0]].weight;
    if (wa !== wb) return wb - wa;
    return TIE_BREAK_ORDER.indexOf(a[0]) - TIE_BREAK_ORDER.indexOf(b[0]);
  });
  return sorted[0][0];
}

function profileForWeakest(weakestKey, answers, skipRules) {
  // Cas energie : distingue recuperation a soutenir et energie a reconstruire.
  if (weakestKey === "energie") {
    const subs = computeEnergySubScores(answers, skipRules);
    const e = subs.energie_sub;
    const r = subs.recuperation_sub;
    if (e !== null && r !== null && r < e) return "recuperation_a_soutenir";
    return "energie_a_reconstruire";
  }
  return PROFILE_BY_WEAKEST_DIMENSION[weakestKey] || "constance_a_reconstruire";
}

export function determineProfile(dimensionScores, answers, readyThreshold, weights, skipRules) {
  const threshold = readyThreshold !== undefined && readyThreshold !== null
    ? readyThreshold
    : DEFAULT_SCORING_CONFIG.profile_ready_threshold;
  const w = weights || DEFAULT_SCORING_CONFIG.dimension_weights;
  const computable = Object.entries(dimensionScores).filter(([, v]) => v !== null && v !== undefined);
  if (computable.length === 0) return "profil_equilibre";
  if (computable.every(([, v]) => v >= threshold)) return "profil_equilibre";

  const restitution = computable.filter(([k]) => RESTITUTION_DIMENSIONS.includes(k));
  if (restitution.length === 0) return "profil_equilibre";

  return profileForWeakest(weakestRestitutionKey(restitution, w), answers, skipRules);
}

export function selectForcesAndPriorities(dimensionScores) {
  const computable = Object.entries(dimensionScores).filter(([, v]) => v !== null && v !== undefined);

  const byDesc = [...computable].sort((a, b) => {
    if (a[1] !== b[1]) return b[1] - a[1];
    return DIMENSIONS.findIndex((d) => d.key === a[0]) - DIMENSIONS.findIndex((d) => d.key === b[0]);
  });
  const forces = byDesc.slice(0, 2).map(([k]) => k);

  const restitution = computable.filter(([k]) => RESTITUTION_DIMENSIONS.includes(k) && !forces.includes(k));
  const byAsc = [...restitution].sort((a, b) => {
    if (a[1] !== b[1]) return a[1] - b[1];
    const wa = DIMENSION_INDEX[a[0]].weight;
    const wb = DIMENSION_INDEX[b[0]].weight;
    if (wa !== wb) return wb - wa;
    return TIE_BREAK_ORDER.indexOf(a[0]) - TIE_BREAK_ORDER.indexOf(b[0]);
  });
  const priorities = byAsc.slice(0, 2).map(([k]) => k);

  if (priorities.length < 2) {
    const remaining = [...computable]
      .sort((a, b) => a[1] - b[1])
      .map(([k]) => k)
      .filter((k) => !priorities.includes(k));
    for (const k of remaining) {
      priorities.push(k);
      if (priorities.length === 2) break;
    }
  }
  return { forces, priorities };
}

function completeActions(actions, poolKeys, lib) {
  // Complete la liste a 3 actions sans doublon, a partir des viviers donnes.
  for (const poolKey of poolKeys) {
    const pool = lib[poolKey] || ACTIONS_LIBRARY[poolKey] || [];
    for (const action of pool) {
      if (actions.length >= 3) break;
      if (!actions.includes(action)) actions.push(action);
    }
  }
  return actions.slice(0, 3);
}

export function selectActions(profileKey, priorities, library) {
  const lib = library || ACTIONS_LIBRARY;
  if (profileKey === "profil_equilibre" || !priorities || priorities.length === 0) {
    return (lib.prete || ACTIONS_LIBRARY.prete).slice(0, 3);
  }
  const actions = [];
  const first = lib[priorities[0]] || [];
  actions.push(...first.slice(0, 2));
  if (priorities.length > 1) {
    const second = lib[priorities[1]] || [];
    actions.push(...second.slice(0, 1));
  }
  return completeActions(actions, [priorities[0], "prete"], lib);
}

/* ------------------------------------------------------------------ */
/* Construction du bilan complet (parité stricte avec build_result)     */
/* ------------------------------------------------------------------ */

const OPENING_SENTENCE =
  "Tes réponses suggèrent que ta progression dépend moins d'un manque de motivation " +
  "que de l'alignement entre tes habitudes, ton niveau d'énergie et ta récupération.";

function buildIntroText(medicalCaution, priorityPhrases) {
  // Texte d'introduction du bilan selon la branche (prudence, 2, 1 ou 0 priorités).
  if (medicalCaution) {
    return (
      "Certaines de tes réponses indiquent qu'il est préférable de clarifier ta situation " +
      "avant de modifier significativement ta pratique physique. CYCLIQ ne remplace pas un " +
      "avis médical. Nous te recommandons de solliciter le professionnel de santé qui " +
      "connaît ta situation afin de déterminer les conditions de pratique adaptées. " +
      "Ton bilan reste un outil de compréhension de ton fonctionnement."
    );
  }
  if (priorityPhrases.length >= 2) {
    return (
      OPENING_SENTENCE + " " +
      `Dans ton cas, les leviers prioritaires semblent être ${priorityPhrases[0]}, ainsi que ${priorityPhrases[1]}. ` +
      "La bonne nouvelle : ces éléments peuvent être structurés progressivement avec une stratégie adaptée à ton quotidien."
    );
  }
  if (priorityPhrases.length === 1) {
    return (
      OPENING_SENTENCE + " " +
      `Dans ton cas, le levier prioritaire semble être ${priorityPhrases[0]}. ` +
      "La bonne nouvelle : cet élément peut être structuré progressivement avec une stratégie adaptée à ton quotidien."
    );
  }
  return (
    "Tes réponses dessinent une base solide. La prochaine étape : identifier les ajustements " +
    "les plus pertinents pour ton fonctionnement personnel."
  );
}

function selectFinalActions(medicalCaution, profileKey, priorityKeys, actionsLibrary) {
  // Branche prudence -> actions d'orientation uniquement, sinon sélection standard.
  if (medicalCaution) {
    const lib = actionsLibrary || ACTIONS_LIBRARY;
    return (lib.prudence || ACTIONS_LIBRARY.prudence).slice(0, 3);
  }
  return selectActions(profileKey, priorityKeys, actionsLibrary);
}


export function buildResult(answers, options = {}) {
  const { actionsLibrary = null, scoringConfig = null, skipRules = DEFAULT_SKIP_RULES } = options;
  const config = mergedConfig(scoringConfig);
  const weights = config.dimension_weights;

  const dimensionScores = computeDimensionScores(answers, skipRules);
  const globalScore = computeGlobalScore(dimensionScores, weights);
  const profileKey = determineProfile(
    dimensionScores, answers, config.profile_ready_threshold, weights, skipRules
  );

  const noCycle = ["non", "contraception"].includes(answers.q4);
  const safetyFlag = computeSafetyFlag(answers);
  const medicalCaution = requiresCaution(safetyFlag);

  const { forces: forcesKeys, priorities: priorityKeys } = selectForcesAndPriorities(dimensionScores);

  const dimPayload = (key, kind) => {
    const dim = DIMENSION_INDEX[key];
    return {
      key,
      label: dim.label,
      score: dimensionScores[key] !== undefined ? dimensionScores[key] : null,
      text: kind === "force" ? dim.force_text : dim.priority_text,
    };
  };

  const phraseField = noCycle ? "phrase_no_cycle" : "phrase";
  const priorityPhrases = priorityKeys.map((k) => DIMENSION_INDEX[k][phraseField]);

  const introText = buildIntroText(medicalCaution, priorityPhrases);
  const actions = selectFinalActions(medicalCaution, profileKey, priorityKeys, actionsLibrary);

  const answered = ALL_QUESTION_IDS.filter((q) => questionScore(q, answers, skipRules) !== null);

  return {
    dimensions: DIMENSIONS.map((dim) => ({
      key: dim.key,
      label: dim.label,
      weight: weights[dim.key] !== undefined ? weights[dim.key] : dim.weight,
      score: dimensionScores[dim.key] !== undefined ? dimensionScores[dim.key] : null,
    })),
    global_score: globalScore,
    profile: {
      key: profileKey,
      label: PROFILES[profileKey].label,
      description: PROFILES[profileKey].description,
    },
    intro_text: introText,
    forces: forcesKeys.map((k) => dimPayload(k, "force")),
    priorites: priorityKeys.map((k) => dimPayload(k, "priority")),
    actions,
    flags: { no_cycle: noCycle, medical_caution: medicalCaution, safety_flag: safetyFlag },
    cta_variant: medicalCaution ? "prudent" : "direct",
    scored_questions_count: answered.length,
    bareme_version: config.bareme_version || BAREME_VERSION,
    created_at: new Date().toISOString(),
  };
}
